import * as Sentry from "@sentry/react";
import { Alert, Drawer, Flex, Row, message } from "antd";
import queryString from "query-string";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Outlet } from "react-router-dom";
import Swal from "sweetalert2";
import CreateClient from "../../components/CreateClient/CreateClient.component";
import CreateNoteModalGCS from "../../components/CreateNoteModal/CreateNoteModalGCS.component";
import CustomiseNote from "../../components/Customise/customise-note.component";
import FeatureRestricted from "../../components/FeatureRestricted/FeatureRestricted";
import PatientSyncModal from "../../components/PatientSyncNotice/PatientSyncModal.component";
import Share from "../../components/Share/Share.modal";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { getAllClients, getAllDcmCodes } from "../../service/client.service";
import { getCustomizationList, handleCancelRecording } from "../../service/notes.service";
import { resumableUploadManager } from "../../service/upload.service";
import {
  setAddClientView,
  setCreateClientState,
  setCreateModalState,
  setCustomiseSectionState,
  setDemoState,
  setEditingState,
  setIsDemoPath,
  setMobilePath,
  setSelectedNoteForEditing,
  setShowRecordingView,
  setSubmissionProgressState,
  setUploadProgressState,
  setUploadType,
} from "../../slices/appStateSlice";
import { cacheClients, setClientForm, setCustomizationList, setDsm } from "../../slices/clientSlice";
import { setIsPaused, setIsRecording, setMediaRecorder, setRecordingDetail } from "../../slices/recordingsSlice";
import type { AppDispatch } from "../../store";
import { EventType, trackEvent } from "../../utils/analytics";
import { MAX_RECORDING_TIME_SECONDS } from "../../utils/constants";
import { sortClientsByName } from "../../utils/datamanipulation.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import { isTooShort, stopMediaRecorderAndStopTracks, waitForMediaRecorderStop } from "../../utils/recording.utils";
import { releaseWakeLock, requestWakeLock } from "../../utils/wakeScreen";
import styles from "./home.module.scss";
import { checkMicrophoneMuteStatus } from "./utils";

const NotesForMobile = () => {
  const { token } = queryString.parse(window.location.search);

  if (token && localStorage.getItem("token") !== token) {
    localStorage.setItem("token", token as string);
  }

  const componentName = "home";
  const dispatch = useDispatch<AppDispatch>();
  const isMobileView = useIsMobileView();

  const { isCustomiseSectionVisible, uploadType, isCreateModalVisible, isCreateClientDrawerVisible, demoState } =
    useAppSelector((state) => state.appState);

  const { mediaRecorder, isRecording, recordingDetail } = useAppSelector((state) => state.recordings);
  const { sessionTerm, sessionTermLower } = useSpecialityReference();

  const [recordingTime, setRecordingTime] = React.useState<number>(0);
  const captureRecordingIdRef = React.useRef("");
  const recordingInterval = React.useRef<NodeJS.Timeout | null>(null);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = React.useState(false);
  const createNoteModalRef = React.useRef<{
    cleanupAudioResources?: () => void;
    stopMicTracks?: () => void;
    clearFileUploads?: () => void;
    awaitPendingDataHandlers?: () => Promise<void>;
  } | null>(null);

  const populateStore = () => {
    getAllDcmCodes()
      .then((response) => {
        const dsm = response.data;
        dispatch(setDsm(dsm));
      })
      .catch((error) => {
        console.error("Error fetching DCM codes:", error);
      });
    getCustomizationList()
      .then((response) => {
        dispatch(setCustomizationList(response.data));
      })
      .catch((error) => {
        console.error("Error fetching customization list:", error);
      });
  };

  const fetchClients = useCallback(() => {
    getAllClients()
      .then((response) => {
        const ownedClients = sortClientsByName(response.data.owned || []);
        const shared = sortClientsByName(response.data.shared || []);
        // Hydrate the byId cache (pickers/sidebar fetch their own paged data; no old array writes).
        dispatch(cacheClients([...ownedClients, ...shared]));
      })
      .catch((error) => {
        console.error(error);
      });
  }, [dispatch]);

  React.useEffect(() => {
    fetchClients();
    dispatch(setMobilePath(true));
  }, [dispatch, fetchClients]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: populateStore updates every render
  React.useEffect(() => {
    dispatch(setIsDemoPath(false));
    populateStore();
  }, [dispatch]);

  const resetRecordingState = (withoutRefresh = false) => {
    if (mediaRecorder?.state !== "inactive") {
      stopMediaRecorderAndStopTracks(mediaRecorder);
    }

    // Stop all mic tracks first (including orphaned ones in the registry)
    createNoteModalRef.current?.stopMicTracks?.();

    // Cleanup audio resources in CreateNoteModal if available
    if (createNoteModalRef.current?.cleanupAudioResources) {
      createNoteModalRef.current.cleanupAudioResources();
    }

    // Clear file uploads (audio and PDFs) in CreateNoteModal if available
    if (createNoteModalRef.current?.clearFileUploads) {
      createNoteModalRef.current.clearFileUploads();
    }

    dispatch(setShowRecordingView(false));
    dispatch(setSubmissionProgressState(false));
    dispatch(setMediaRecorder(null));
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }
    setRecordingTime(0);
    dispatch(setUploadType(0));
    captureRecordingIdRef.current = "";
    resumableUploadManager.clearSession();
    dispatch(
      setRecordingDetail({
        title: "",
        note_type: undefined,
        category_type: 0,
        modality_type: "audio",
        gender_type: 0,
        language_type: 0,
        client_id: null,
        current_recording_id: null,
        fileList: [],
      }),
    );
    dispatch(setCreateModalState(false));
    dispatch(setEditingState(false));
    dispatch(setSelectedNoteForEditing(null));
    dispatch(setIsRecording(false));
    dispatch(setIsPaused(false));
    if (!withoutRefresh) {
      eventEmitter.emit("refreshNotes");
    }
    dispatch(setUploadProgressState(false));
  };

  const handleCancel = async () => {
    console.log("Handling retry/cancel...");
    const audioIdToCancel = captureRecordingIdRef.current || resumableUploadManager.getAudioId()?.toString();

    if (audioIdToCancel) {
      try {
        await handleCancelRecording({ audio_id: audioIdToCancel, status: "cancel" });
        console.log("Backend notified of cancellation via handleRetry for audio ID:", audioIdToCancel);
      } catch (error) {
        console.error("Error notifying backend of cancellation via handleRetry:", error);
        Sentry.captureException(error, { extra: { audio_id: audioIdToCancel } });
      }
    } else {
      console.warn("handleRetry called without an apparent audio ID to cancel.");
    }
    if (mediaRecorder?.state !== "inactive") {
      stopMediaRecorderAndStopTracks(mediaRecorder);
    }
    resetRecordingState();
    message.info(`${sessionTerm} reset.`);
  };

  const displayRecordingTimer = () => {
    // Start a timer to track recording time
    recordingInterval.current = setInterval(() => {
      if (recordingTime >= MAX_RECORDING_TIME_SECONDS) {
        return recordingTime;
      }
      setRecordingTime((time) => time + 1);
    }, 1000);
  };

  React.useEffect(() => {
    if (isRecording && recordingTime >= MAX_RECORDING_TIME_SECONDS) {
      handlePauseOrEndRecording();
      handleSubmitAudio();
    }
  }, [isRecording, recordingTime]);

  const handlePauseOrEndRecording = async () => {
    await releaseWakeLock();
    dispatch(setIsRecording(false));
    if (mediaRecorder?.state === "inactive") {
      return;
    }
    trackEvent(EventType.STOP_BUTTON, {
      modalityMode: recordingDetail.modality_type,
    });
    dispatch(setIsPaused(true));
    mediaRecorder?.pause();
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }
  };

  const handleSubmitAudio = async () => {
    try {
      if (isTooShort(recordingTime)) {
        Swal.fire({
          icon: "error",
          title: "Recording Too Short",
          text: "This recording is too short. Please record for at least 15 seconds, then try again.",
        });
        return;
      }
      dispatch(setSubmissionProgressState(true));
      trackEvent(EventType.SUBMIT_AUDIO_MODAL, {
        modalityMode: recordingDetail.modality_type,
      });

      let recorderStopPromise = Promise.resolve();
      if (mediaRecorder?.state !== "inactive") {
        recorderStopPromise = waitForMediaRecorderStop(mediaRecorder);
        stopMediaRecorderAndStopTracks(mediaRecorder);
      }

      // Release mic hardware immediately (fire-and-forget, does not block submission)
      createNoteModalRef.current?.stopMicTracks?.();

      await recorderStopPromise;
      await completeSubmissionGCS();
    } catch (error) {
      dispatch(setSubmissionProgressState(false));
      Sentry.captureException(error);
      Swal.fire({
        icon: "error",
        title: "Error submitting audio",
        text: "Please try again or contact support.",
      });
    }
  };

  React.useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, []);

  /**
   * If media Recorder is not null, add an error event listener to it
   */
  React.useEffect(() => {
    if (mediaRecorder) {
      mediaRecorder.addEventListener("error" as any, (event: any) => {
        Sentry.captureException(event.error);
      });
    }

    return () => {
      if (mediaRecorder) {
        mediaRecorder.removeEventListener("error" as any, () => {
          console.log("error event listener removed");
        });
      }
    };
  }, [mediaRecorder]);

  const completeSubmissionGCS = async (isLocked = false) => {
    if (uploadType === 0 && resumableUploadManager.hasActiveSession()) {
      console.log("Finalizing resumable upload submission");
      try {
        await createNoteModalRef.current?.awaitPendingDataHandlers?.();
        await resumableUploadManager.finalizeUpload(isLocked);

        trackEvent(EventType.SUBMIT_AUDIO_FINAL, {
          audioFileId: resumableUploadManager.getAudioId()?.toString(),
          modalityMode: recordingDetail.modality_type,
          uploadMethod: "resumable",
        });
        message.success(`${sessionTerm} submitted successfully. Processing in background...`);
      } catch (error) {
        console.error("Error finalizing resumable upload:", error);
        Sentry.captureException(error);
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "We couldn't finalize the submission. Please try again later or contact support.",
        });
        dispatch(setSubmissionProgressState(false));
      } finally {
        resetRecordingState();
      }
    } else if (uploadType === 0 && !resumableUploadManager.hasActiveSession()) {
      console.error("Attempted to complete submission but no active resumable session found.");
      Sentry.captureMessage("Submission attempt without active resumable session", {
        level: "warning",
      });
      Swal.fire({
        icon: "error",
        title: "Submission Error",
        text: `Cannot submit the ${sessionTermLower} due to an internal error. Please cancel and restart.`,
      });
      dispatch(setSubmissionProgressState(false));
    } else {
      console.warn("completeSubmission called for unexpected uploadType:", uploadType);
      if (uploadType === 0) resetRecordingState();
    }
  };

  React.useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("Page is now in the foreground!");
        await requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  React.useEffect(() => {
    const { isNoteModalOpen, isClientModalOpen, recordingDetails, clientForm } = demoState;

    if (isNoteModalOpen || isClientModalOpen) {
      if (isNoteModalOpen) {
        dispatch(setRecordingDetail(recordingDetails));
        dispatch(setCreateModalState(true));
      }
      if (isClientModalOpen) {
        dispatch(setCreateClientState(true));
      }
      if (clientForm) {
        dispatch(setAddClientView(true));
        dispatch(setClientForm(clientForm));
      }
      dispatch(
        setDemoState({
          isNoteModalOpen: false,
          recordingDetails: null,
          clientForm: null,
          isClientModalOpen: false,
        }),
      );
    }
  }, [demoState, dispatch]);

  return (
    <>
      {isMicrophoneMuted && <Alert message="Microphone is muted. Please unmute to record properly." banner closable />}
      <PatientSyncModal />
      <Row className={styles[`${componentName}__container`]}>
        {isCreateClientDrawerVisible && <CreateClient />}
        {isCreateModalVisible && (
          <CreateNoteModalGCS
            isCaptureEnabled={true}
            captureRecordingIdRef={captureRecordingIdRef}
            resetRecordingState={resetRecordingState}
            handleCancel={handleCancel}
            setRecordingTime={setRecordingTime}
            displayRecordingTimer={displayRecordingTimer}
            checkMicrophoneMuteStatus={(stream: MediaStream) => checkMicrophoneMuteStatus(stream, setIsMicrophoneMuted)}
            getCaptureDetails={() => {
              return {};
            }}
            recordingTime={recordingTime}
            ref={createNoteModalRef}
          />
        )}
        <Share />
      </Row>
      <Drawer
        title="Customize Note"
        open={isCustomiseSectionVisible}
        placement={isMobileView ? "bottom" : "right"}
        height={isMobileView ? "85%" : "100%"}
        closable={true}
        destroyOnClose
        onClose={() => dispatch(setCustomiseSectionState(false))}
      >
        <Flex
          vertical
          justify="space-between"
          style={{
            height: "100%",
          }}
        >
          <Flex className={`${styles[`${componentName}__customise_clearance`]}`}>
            <span />
          </Flex>
          <CustomiseNote noPadding={true} />
        </Flex>
      </Drawer>
      <div
        id="notes-container-mobile"
        className={styles[`${componentName}__container-left`]}
        style={{
          height: "100vh",
          maxHeight: "100vh",
          width: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Outlet />
      </div>
      <FeatureRestricted />
    </>
  );
};

export default NotesForMobile;
